<template>
  <div class="recon-processing">
    <EntraPageHeader
      title="Recon Processing"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadStatus"
    />
    <div class="page-body">
      <div v-if="error" class="error-message">{{ error }}</div>
      <div v-if="successMsg" class="success-message">{{ successMsg }}</div>

      <div class="card">
        <h3>Step 1 — Process Recon Data</h3>
        <p class="step-desc">Transforms raw billing line items into enriched processed data (safe to re-run — whole-month delete+rebuild). The nightly 02:00 UTC run does this automatically; use this after config changes. Both M365 and Azure are processed.</p>
        <div v-if="pricingBlocked" class="pricing-gate-banner">
          <strong>NCE pricing blocked for {{ form.billingMonth }}:</strong> missing market files
          {{ formPricingReadiness.missingMarkets.join(', ') }} (expected 6). Upload the month set on
          Recon Config &rarr; Monthly &rarr; NCE Prices before processing.
        </div>
        <div class="process-form">
          <div class="filter-group">
            <label class="filter-label">Billing Month:</label>
            <input v-model="form.billingMonth" type="month" class="filter-input" @change="loadPricingReadiness" />
          </div>
          <div class="filter-group">
            <label class="filter-label">Region (optional):</label>
            <input v-model="form.regionCode" type="text" class="filter-input" placeholder="All regions" />
          </div>
          <button class="process-btn" :disabled="processing || pricingBlocked" @click="triggerProcess">
            {{ processing ? 'Processing...' : 'Process Recon' }}
          </button>
        </div>
        <div v-if="processResult" class="process-result" :class="{ 'process-error': hasProcessErrors }">
          <div v-if="processResult.m365" class="result-block">
            <span class="type-badge m365">M365</span>
            <span class="result-row">Raw: {{ processResult.m365.rawRowCount }}</span>
            <span class="result-row">Trimmed: {{ processResult.m365.trimmedRowCount }}</span>
            <span class="result-row">Processed: {{ processResult.m365.processedRowCount }}</span>
            <span class="result-row">Clients: {{ processResult.m365.clientCount }}</span>
            <span class="result-row error" v-if="processResult.m365.errors.length > 0">Errors: {{ processResult.m365.errors.length }}</span>
            <div v-if="processResult.m365.errors.length > 0" class="error-list">
              <div v-for="(err, i) in processResult.m365.errors.slice(0, 5)" :key="i">{{ err }}</div>
            </div>
          </div>
          <div v-if="processResult.azure" class="result-block">
            <span class="type-badge azure">Azure</span>
            <span class="result-row">Raw: {{ processResult.azure.rawRowCount }}</span>
            <span class="result-row">Trimmed: {{ processResult.azure.trimmedRowCount }}</span>
            <span class="result-row">Processed: {{ processResult.azure.processedRowCount }}</span>
            <span class="result-row">Clients: {{ processResult.azure.clientCount }}</span>
            <span class="result-row error" v-if="processResult.azure.errors.length > 0">Errors: {{ processResult.azure.errors.length }}</span>
            <div v-if="processResult.azure.errors.length > 0" class="error-list">
              <div v-for="(err, i) in processResult.azure.errors.slice(0, 5)" :key="i">{{ err }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <h3>Step 2 — Select Processed Month</h3>
        <p class="step-desc">Click a month to work with. Generation and files are per-month (M365 + Azure together).</p>
        <div v-if="statusState === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="processedMonths.length > 0" class="trend-table">
          <table class="data-table">
            <thead>
              <tr>
                <th>Billing Month</th>
                <th style="text-align: right;">M365 Lines</th>
                <th style="text-align: right;">M365 Clients</th>
                <th style="text-align: right;">Azure Lines</th>
                <th style="text-align: right;">Azure Clients</th>
                <th>Processed At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in processedMonths" :key="m.billing_month"
                  :class="{ selected: selectedMonth && selectedMonth.billing_month === m.billing_month }"
                  @click="selectMonth(m)">
                <td><strong>{{ m.billing_month }}</strong></td>
                <td style="text-align: right;">{{ m.m365 ? m.m365.line_item_count : '—' }}</td>
                <td style="text-align: right;">{{ m.m365 ? m.m365.client_count : '—' }}</td>
                <td style="text-align: right;">{{ m.azure ? m.azure.line_item_count : '—' }}</td>
                <td style="text-align: right;">{{ m.azure ? m.azure.client_count : '—' }}</td>
                <td>{{ formatDate(m.processed_at, 'short') }}</td>
                <td><button class="summary-btn" @click.stop="selectMonth(m)">Select</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No recon data processed yet. Use Step 1 above to process a month.</p>
      </div>

      <div class="card" v-if="selectedMonth">
        <div class="card-header-row">
          <h3>Step 3 — Generate Month Set — {{ selectedMonth.billing_month }}</h3>
        </div>
        <div v-if="attention && (attention.unlisted.length > 0 || attention.incomplete.length > 0)" class="attention-banner">
          <strong>Attention needed before generating (M365 + Azure):</strong>
          <div v-if="attention.unlisted.length > 0">
            {{ attention.unlisted.length }} unlisted client{{ attention.unlisted.length === 1 ? '' : 's' }} (no Client Billing Dates entry — flagged green by the macro):
            {{ attention.unlisted.join(', ') }}
          </div>
          <div v-if="attention.incomplete.length > 0">
            {{ attention.incomplete.length }} client{{ attention.incomplete.length === 1 ? '' : 's' }} with incomplete finance config:
            <span v-for="(c, i) in attention.incomplete" :key="c.clientName">{{ i > 0 ? ', ' : '' }}{{ c.clientName }} (missing {{ c.missing.join(', ') }})</span>
          </div>
          <div class="attention-hint">Configure these on the Recon Config page (Client Config tab). Generation is not blocked.</div>
        </div>
        <div v-if="pricingSelectedBlocked" class="pricing-gate-banner">
          <strong>NCE pricing blocked for {{ selectedMonth.billing_month }}:</strong> generation is disabled until the
          month set (6 market files) is uploaded on Recon Config &rarr; Monthly &rarr; NCE Prices. Missing:
          {{ selectedPricingReadiness.missingMarkets.join(', ') }}.
        </div>
        <div v-else-if="pricingBypassNote" class="pricing-gate-note">{{ pricingBypassNote }}</div>
        <div class="gen-hero-row">
          <button class="generate-hero-btn" :disabled="outputsGenerating || pricingSelectedBlocked" @click="generateOutputs">
            {{ outputsGenerating ? 'Generating…' : 'Generate Month Set' }}
          </button>
          <span class="gen-hero-desc">Per-client detail files (both types) + per-entity Intacct AR uploads + per-entity AP uploads — versioned and persisted. Re-running creates new versions, never overwrites.</span>
        </div>
        <div v-if="lastRun" class="lastrun-row" :class="lastRun.status">
          Last run {{ lastRun.status }}
          <span v-if="lastRun.generatedAt"> — {{ formatDate(lastRun.generatedAt, 'short') }}</span>
          <div v-if="lastRun.status === 'failed' && lastRunError" class="lastrun-error">{{ lastRunError }}</div>
        </div>
      </div>

      <div class="card" v-if="selectedMonth">
        <div class="card-header-row">
          <h3>Generated Files — {{ selectedMonth.billing_month }}</h3>
          <div class="header-actions">
            <label class="toggle-label"><input type="checkbox" v-model="showAllVersions" /> Show all versions</label>
            <button v-if="isAdmin && lastRun" class="delete-run-btn" @click="deleteRun(lastRun)">
              Delete run ({{ lastRun.runId.slice(0, 8) }}…)
            </button>
          </div>
        </div>
        <div v-if="spotChecks && spotChecks.length > 0" class="spotcheck-strip">
          <div v-for="(item, idx) in spotChecks" :key="idx" class="spotcheck-item" :class="item.severity">
            <strong>{{ item.check }}</strong><span v-if="item.client"> — {{ item.client }}</span>: {{ item.detail }}
          </div>
        </div>
        <div v-if="outputsState === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="visibleOutputs.length > 0" class="config-table-wrap">
          <div class="outputs-toolbar">
            <button class="toolbar-btn" @click="downloadBundle('client_detail', 'M365')">M365 Client Details (zip)</button>
            <button class="toolbar-btn" @click="downloadBundle('client_detail', 'Azure')">Azure Client Details (zip)</button>
            <button class="toolbar-btn" @click="downloadBundle('intacct_upload', 'M365')">M365 Intacct (zip)</button>
            <button class="toolbar-btn" @click="downloadBundle('intacct_upload', 'Azure')">Azure Intacct (zip)</button>
            <button class="toolbar-btn" @click="downloadBundle('ap_upload')">AP Uploads (zip)</button>
          </div>
          <div class="outputs-filters">
            <input v-model="outputsFilter.text" class="filter-input outputs-filter-text" placeholder="Filter file or client…" />
            <select v-model="outputsFilter.kind" class="filter-select">
              <option value="">All kinds</option>
              <option v-for="k in outputKindOptions" :key="k" :value="k">{{ kindLabel(k) }}</option>
            </select>
            <select v-model="outputsFilter.reconType" class="filter-select">
              <option value="">All types</option>
              <option v-for="t in outputTypeOptions" :key="t" :value="t">{{ t || '—' }}</option>
            </select>
            <select v-model="outputsFilter.runStatus" class="filter-select">
              <option value="">All statuses</option>
              <option v-for="s in outputStatusOptions" :key="s" :value="s">{{ s }}</option>
            </select>
            <span class="outputs-count" v-if="filteredSortedOutputs.length !== visibleOutputs.length">
              {{ filteredSortedOutputs.length }} of {{ visibleOutputs.length }} files
            </span>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th class="sortable" @click="sortOutputs('billingMonth')">Month <span class="sort-ind">{{ outputsSortIndicator('billingMonth') }}</span></th>
                <th class="sortable" @click="sortOutputs('fileKind')">Kind <span class="sort-ind">{{ outputsSortIndicator('fileKind') }}</span></th>
                <th class="sortable" @click="sortOutputs('reconType')">Type <span class="sort-ind">{{ outputsSortIndicator('reconType') }}</span></th>
                <th class="sortable" @click="sortOutputs('entity')">Client / Entity <span class="sort-ind">{{ outputsSortIndicator('entity') }}</span></th>
                <th class="sortable" @click="sortOutputs('filename')">File <span class="sort-ind">{{ outputsSortIndicator('filename') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOutputs('version')">Version <span class="sort-ind">{{ outputsSortIndicator('version') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOutputs('byteSize')">Size <span class="sort-ind">{{ outputsSortIndicator('byteSize') }}</span></th>
                <th class="sortable" @click="sortOutputs('runStatus')">Status <span class="sort-ind">{{ outputsSortIndicator('runStatus') }}</span></th>
                <th class="sortable" @click="sortOutputs('generatedAt')">Generated <span class="sort-ind">{{ outputsSortIndicator('generatedAt') }}</span></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredSortedOutputs" :key="item.id" @click="previewOutput(item)">
                <td><strong>{{ item.billingMonth }}</strong></td>
                <td>{{ kindLabel(item.fileKind) }}</td>
                <td>{{ item.reconType || '—' }}</td>
                <td>{{ item.customerName || item.intacctEntity || 'Combined' }}</td>
                <td class="filename-cell">{{ item.filename }}</td>
                <td style="text-align: right;">v{{ item.version }}</td>
                <td style="text-align: right;">{{ (item.byteSize / 1024).toFixed(1) }} KB</td>
                <td><span class="run-badge" :class="item.runStatus">{{ item.runStatus }}</span></td>
                <td>{{ formatDate(item.generatedAt, 'short') }}</td>
                <td>
                  <button class="summary-btn" @click.stop="previewOutput(item)">Preview</button>
                  <button class="summary-btn" @click.stop="downloadOutput(item)">Download</button>
                  <button v-if="isAdmin" class="delete-btn" @click.stop="deleteFile(item)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No files persisted for this month yet. Use Generate Month Set above; ad-hoc previews are under Legacy downloads below.</p>
      </div>

      <div class="card" v-if="selectedMonth">
        <details>
          <summary class="legacy-summary">Legacy preview downloads (unversioned, temporary view, not persisted)</summary>
          <div class="gen-options">
            <label class="toggle-label"><input type="checkbox" v-model="genOptions.includeDnb" /> Include DNB clients</label>
            <label class="toggle-label"><input type="checkbox" v-model="genOptions.includeOtl" /> Include OTL clients</label>
            <label class="toggle-label"><input type="checkbox" v-model="genOptions.splitMonth" /> Split monthly bills</label>
            <label class="toggle-label"><input type="checkbox" v-model="genOptions.timestampFiles" /> Timestamp filenames</label>
          </div>
          <div class="file-gen-buttons">
            <button class="gen-btn" @click="downloadClients">
              <span class="gen-btn-title">Client Workbooks (combined)</span>
              <span class="gen-btn-desc">One review workbook, sheet per client ({{ overviewReconType }} only)</span>
            </button>
            <button class="gen-btn" @click="downloadIntacct">
              <span class="gen-btn-title">Intacct CSV (combined)</span>
              <span class="gen-btn-desc">Multi-entity combined AR upload ({{ overviewReconType }} only)</span>
            </button>
            <button class="gen-btn" @click="downloadAp">
              <span class="gen-btn-title">AP CSV (combined)</span>
              <span class="gen-btn-desc">Combined AP upload — M365 + Azure</span>
            </button>
          </div>
          <p class="gen-note">Combined formats are kept for one billing cycle while accounts transition to the per-entity files above. AP preview keeps the invoice-validation gate (422 on mismatch).</p>
        </details>
      </div>

      <div class="card" v-if="selectedMonth">
        <div class="card-header-row">
          <h3>Overview — {{ selectedMonth.billing_month }}</h3>
          <div class="overview-tabs">
            <button :class="{ active: overviewTab === 'clients' }" @click="setOverviewTab('clients')">Clients</button>
            <button :class="{ active: overviewTab === 'products' }" @click="setOverviewTab('products')">Products</button>
            <button :class="{ active: overviewTab === 'ap' }" @click="setOverviewTab('ap')">AP</button>
          </div>
        </div>
        <div v-if="overviewTab !== 'ap'" class="recon-type-switch">
          <button :class="{ active: overviewReconType === 'M365' }" @click="overviewReconType = 'M365'; loadOverview()">M365</button>
          <button :class="{ active: overviewReconType === 'Azure' }" @click="overviewReconType = 'Azure'; loadOverview()">Azure</button>
        </div>
        <div v-if="overviewState === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="overviewTab === 'clients' && overviewItems && overviewItems.length > 0" class="trend-table">
          <table class="data-table">
            <thead>
              <tr>
                <th class="sortable" @click="sortOverview('customer_name_clean')">Client <span class="sort-ind">{{ overviewSortIndicator('customer_name_clean') }}</span></th>
                <th class="sortable" @click="sortOverview('bill_split')">Bill Split <span class="sort-ind">{{ overviewSortIndicator('bill_split') }}</span></th>
                <th class="sortable" @click="sortOverview('intacct_entity')">Entity <span class="sort-ind">{{ overviewSortIndicator('intacct_entity') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_cost')">Cost <span class="sort-ind">{{ overviewSortIndicator('client_cost') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_charge')">Charge <span class="sort-ind">{{ overviewSortIndicator('client_charge') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_cost_usd')">Cost USD <span class="sort-ind">{{ overviewSortIndicator('client_cost_usd') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_charge_usd')">Charge USD <span class="sort-ind">{{ overviewSortIndicator('client_charge_usd') }}</span></th>
                <th class="sortable" style="text-align: center;" @click="sortOverview('generate_bill')">Bill <span class="sort-ind">{{ overviewSortIndicator('generate_bill') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('line_item_count')">Items <span class="sort-ind">{{ overviewSortIndicator('line_item_count') }}</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in sortedOverviewItems" :key="idx">
                <td>{{ item.customer_name_clean }}</td>
                <td>{{ item.bill_split }}</td>
                <td>{{ item.intacct_entity || '--' }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost_usd, 'USD') }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge_usd, 'USD') }}</td>
                <td style="text-align: center;">{{ item.generate_bill ? 'Yes' : 'No' }}</td>
                <td style="text-align: right;">{{ item.line_item_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="overviewTab === 'products' && overviewItems && overviewItems.length > 0" class="trend-table">
          <table class="data-table">
            <thead>
              <tr>
                <th class="sortable" @click="sortOverview('product_name')">Product <span class="sort-ind">{{ overviewSortIndicator('product_name') }}</span></th>
                <th class="sortable" @click="sortOverview('sku_name')">SKU <span class="sort-ind">{{ overviewSortIndicator('sku_name') }}</span></th>
                <th class="sortable" @click="sortOverview('clean_sku')">Clean SKU <span class="sort-ind">{{ overviewSortIndicator('clean_sku') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('discount_pct')">Discount <span class="sort-ind">{{ overviewSortIndicator('discount_pct') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('total_quantity')">Quantity <span class="sort-ind">{{ overviewSortIndicator('total_quantity') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('total_spend')">Total Spend <span class="sort-ind">{{ overviewSortIndicator('total_spend') }}</span></th>
                <th class="sortable" @click="sortOverview('currency')">Currency <span class="sort-ind">{{ overviewSortIndicator('currency') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('customer_count')">Customers <span class="sort-ind">{{ overviewSortIndicator('customer_count') }}</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in sortedOverviewItems" :key="idx">
                <td>{{ item.product_name }}</td>
                <td>{{ item.sku_name }}</td>
                <td>{{ item.clean_sku || '--' }}</td>
                <td style="text-align: right;">{{ (item.discount_pct * 100).toFixed(2) }}%</td>
                <td style="text-align: right;">{{ item.total_quantity }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.total_spend, item.currency) }}</td>
                <td>{{ item.currency }}</td>
                <td style="text-align: right;">{{ item.customer_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="overviewTab === 'ap' && overviewItems && overviewItems.length > 0" class="trend-table">
          <table class="data-table">
            <thead>
              <tr>
                <th class="sortable" @click="sortOverview('customer_name_clean')">Client <span class="sort-ind">{{ overviewSortIndicator('customer_name_clean') }}</span></th>
                <th class="sortable" style="text-align: center;" @click="sortOverview('generate_bill')">Bill <span class="sort-ind">{{ overviewSortIndicator('generate_bill') }}</span></th>
                <th class="sortable" @click="sortOverview('parent_id')">Parent ID <span class="sort-ind">{{ overviewSortIndicator('parent_id') }}</span></th>
                <th class="sortable" @click="sortOverview('intacct_entity')">Entity <span class="sort-ind">{{ overviewSortIndicator('intacct_entity') }}</span></th>
                <th class="sortable" @click="sortOverview('ap_category')">Category <span class="sort-ind">{{ overviewSortIndicator('ap_category') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_cost')">Cost <span class="sort-ind">{{ overviewSortIndicator('client_cost') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_tax')">Tax <span class="sort-ind">{{ overviewSortIndicator('client_tax') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_charge')">Charge <span class="sort-ind">{{ overviewSortIndicator('client_charge') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_cost_usd')">Cost USD <span class="sort-ind">{{ overviewSortIndicator('client_cost_usd') }}</span></th>
                <th class="sortable" style="text-align: right;" @click="sortOverview('client_charge_usd')">Charge USD <span class="sort-ind">{{ overviewSortIndicator('client_charge_usd') }}</span></th>
                <th class="sortable" @click="sortOverview('invoice_period')">Invoice Period <span class="sort-ind">{{ overviewSortIndicator('invoice_period') }}</span></th>
                <th class="sortable" @click="sortOverview('invoice_csp')">CSP <span class="sort-ind">{{ overviewSortIndicator('invoice_csp') }}</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in sortedOverviewItems" :key="idx">
                <td>{{ item.customer_name_clean }}</td>
                <td style="text-align: center;">{{ item.generate_bill ? 'Yes' : 'No' }}</td>
                <td>{{ item.parent_id || '--' }}</td>
                <td>{{ item.intacct_entity || '--' }}</td>
                <td>{{ item.ap_category || '--' }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_tax) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost_usd, 'USD') }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge_usd, 'USD') }}</td>
                <td>{{ item.invoice_period || '--' }}</td>
                <td>{{ item.invoice_csp || '--' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No overview data available</p>
      </div>

      <ReconFilePreviewModal
        :show="preview.show"
        :filename="preview.filename"
        :loadData="previewLoader"
        @close="preview.show = false"
        @download="downloadOutput(preview.item)"
      />
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import ReconFilePreviewModal from '@/views/components/ReconFilePreviewModal.vue';
import { formatCurrency } from '@/services/entra-utils.js';
import { formatDate } from '@/services/date-utils.js';
import {
  processRecon,
  getReconStatus,
  getReconClientOverview,
  getReconProductOverview,
  getReconApOverview,
  getReconAttention,
  getReconSpotChecks,
  listReconOutputs,
  listReconOutputRuns,
  generateReconOutputs,
  downloadReconOutputFile,
  downloadReconOutputBundle,
  deleteReconOutput,
  deleteReconOutputRun,
  canDeleteOutputs,
  downloadReconClientWorkbook,
  downloadReconIntacctCsv,
  downloadReconApCsv,
  getNcePricingReadiness
} from '@/services/billing-api.js';

function numVal(v) {
  if (v === null || v === undefined || v === '') return '';
  var n = Number(v);
  return Number.isFinite(n) ? n : '';
}

function compareRows(items, getter, order) {
  var sorted = items.slice().sort(function(a, b) {
    var av = getter(a);
    var bv = getter(b);
    var an = (av === null || av === undefined || av === '');
    var bn = (bv === null || bv === undefined || bv === '');
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    var cmp;
    if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv;
    } else {
      cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    }
    return order === 'asc' ? cmp : -cmp;
  });
  return sorted;
}

var OVERVIEW_SORT_GETTERS = {
  clients: {
    customer_name_clean: function(i) { return i.customer_name_clean; },
    bill_split: function(i) { return i.bill_split; },
    intacct_entity: function(i) { return i.intacct_entity; },
    client_cost: function(i) { return numVal(i.client_cost); },
    client_charge: function(i) { return numVal(i.client_charge); },
    client_cost_usd: function(i) { return numVal(i.client_cost_usd); },
    client_charge_usd: function(i) { return numVal(i.client_charge_usd); },
    generate_bill: function(i) { return i.generate_bill ? 1 : 0; },
    line_item_count: function(i) { return numVal(i.line_item_count); }
  },
  products: {
    product_name: function(i) { return i.product_name; },
    sku_name: function(i) { return i.sku_name; },
    clean_sku: function(i) { return i.clean_sku; },
    discount_pct: function(i) { return numVal(i.discount_pct); },
    total_quantity: function(i) { return numVal(i.total_quantity); },
    total_spend: function(i) { return numVal(i.total_spend); },
    currency: function(i) { return i.currency; },
    customer_count: function(i) { return numVal(i.customer_count); }
  },
  ap: {
    customer_name_clean: function(i) { return i.customer_name_clean; },
    generate_bill: function(i) { return i.generate_bill ? 1 : 0; },
    parent_id: function(i) { return i.parent_id; },
    intacct_entity: function(i) { return i.intacct_entity; },
    ap_category: function(i) { return i.ap_category; },
    client_cost: function(i) { return numVal(i.client_cost); },
    client_tax: function(i) { return numVal(i.client_tax); },
    client_charge: function(i) { return numVal(i.client_charge); },
    client_cost_usd: function(i) { return numVal(i.client_cost_usd); },
    client_charge_usd: function(i) { return numVal(i.client_charge_usd); },
    invoice_period: function(i) { return i.invoice_period; },
    invoice_csp: function(i) { return i.invoice_csp; }
  }
};

export default {
  name: 'ReconProcessing',
  components: { EntraPageHeader, ReconFilePreviewModal },
  computed: {
    pricingBlocked: function() {
      var r = this.formPricingReadiness;
      return !!(r && r.blocked && r.billingMonth === this.form.billingMonth);
    },
    pricingSelectedBlocked: function() {
      var r = this.selectedPricingReadiness;
      return !!(r && r.blocked && this.selectedMonth && r.billingMonth === this.selectedMonth.billing_month);
    },
    pricingBypassNote: function() {
      var r = this.selectedPricingReadiness;
      if (!r || !this.selectedMonth || r.billingMonth !== this.selectedMonth.billing_month || r.complete) return '';
      if (r.bypassReason === 're-process') return 'NCE pricing set for ' + r.billingMonth + ' is incomplete (' + r.rowCount + ' rows present; missing markets: ' + r.missingMarkets.join(', ') + '). Allowed because this month is already processed — rows price from the latest effective-dated set.';
      if (r.bypassReason === 'gate-off') return 'Pricing gate is disabled via Default Config (pricing_gate_enforced = off) — running with an incomplete ' + r.billingMonth + ' price set.';
      return '';
    },
    hasProcessErrors: function() {
      if (!this.processResult) return false;
      var m365 = this.processResult.m365;
      var azure = this.processResult.azure;
      return ((m365 && m365.errors && m365.errors.length > 0) || (azure && azure.errors && azure.errors.length > 0)) ? true : false;
    },
    processedMonths: function() {
      var map = {};
      (this.statusItems || []).forEach(function(item) {
        var m = map[item.billing_month];
        if (!m) {
          m = { billing_month: item.billing_month, m365: null, azure: null, processed_at: item.processed_at };
          map[item.billing_month] = m;
        }
        if (item.recon_type === 'M365') m.m365 = item;
        if (item.recon_type === 'Azure') m.azure = item;
        if (item.processed_at > m.processed_at) m.processed_at = item.processed_at;
      });
      return Object.keys(map).sort(function(a, b) { return b.localeCompare(a); }).map(function(k) { return map[k]; });
    },
    visibleOutputs: function() {
      if (this.showAllVersions) return this.outputs;
      var latest = {};
      this.outputs.forEach(function(o) {
        var key = [o.fileKind, o.reconType || '', o.intacctEntity || '', o.customerName || ''].join('|');
        if (!latest[key] || o.version > latest[key].version) latest[key] = o;
      });
      return Object.keys(latest).map(function(k) { return latest[k]; });
    },
    outputKindOptions: function() {
      var seen = {};
      this.visibleOutputs.forEach(function(o) { if (o.fileKind) seen[o.fileKind] = true; });
      return Object.keys(seen).sort();
    },
    outputTypeOptions: function() {
      var seen = {};
      this.visibleOutputs.forEach(function(o) { seen[o.reconType || ''] = true; });
      return Object.keys(seen).sort();
    },
    outputStatusOptions: function() {
      var seen = {};
      this.visibleOutputs.forEach(function(o) { if (o.runStatus) seen[o.runStatus] = true; });
      return Object.keys(seen).sort();
    },
    filteredSortedOutputs: function() {
      var self = this;
      var f = this.outputsFilter;
      var text = f.text.trim().toLowerCase();
      var items = this.visibleOutputs.filter(function(o) {
        if (f.kind && o.fileKind !== f.kind) return false;
        if (f.reconType !== '' && (o.reconType || '') !== f.reconType) return false;
        if (f.runStatus && o.runStatus !== f.runStatus) return false;
        if (text && (o.filename || '').toLowerCase().indexOf(text) === -1 && self.entityLabel(o).toLowerCase().indexOf(text) === -1) return false;
        return true;
      });
      var col = this.outputsSort.column;
      if (!col) return items;
      var getters = {
        billingMonth: function(o) { return o.billingMonth; },
        fileKind: function(o) { return self.kindLabel(o.fileKind); },
        reconType: function(o) { return o.reconType || ''; },
        entity: function(o) { return self.entityLabel(o); },
        filename: function(o) { return o.filename; },
        version: function(o) { return o.version; },
        byteSize: function(o) { return o.byteSize; },
        runStatus: function(o) { return o.runStatus; },
        generatedAt: function(o) { return o.generatedAt || ''; }
      };
      var getter = getters[col];
      if (!getter) return items;
      return compareRows(items, getter, this.outputsSort.order);
    },
    sortedOverviewItems: function() {
      var items = this.overviewItems || [];
      var col = this.overviewSort.column;
      if (!col) return items;
      var getters = OVERVIEW_SORT_GETTERS[this.overviewTab];
      var getter = getters && getters[col];
      if (!getter) return items;
      return compareRows(items, getter, this.overviewSort.order);
    },
    lastRun: function() {
      if (!this.runs || this.runs.length === 0) return null;
      var sorted = this.runs.slice().sort(function(a, b) {
        return String(b.generatedAt).localeCompare(String(a.generatedAt));
      });
      return sorted[0];
    },
    lastRunError: function() {
      if (!this.lastRun) return '';
      var v = this.lastRun.validation;
      if (v && v.error) return v.error;
      var ap = this.outputs.find(function(o) { return o.fileKind === 'ap_upload' && o.validation && o.validation.mismatches && o.validation.mismatches.length > 0; });
      if (ap) return 'AP invoice validation: ' + ap.validation.mismatches.join('; ');
      return '';
    }
  },
  data: function() {
    return {
      error: '',
      successMsg: '',
      processing: false,
      processResult: null,
      form: {
        billingMonth: (function() {
          var now = new Date();
          return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString().slice(0, 7);
        })(),
        regionCode: ''
      },
      genOptions: {
        includeDnb: false,
        includeOtl: false,
        splitMonth: false,
        timestampFiles: false
      },
      statusState: 'loading',
      statusItems: [],
      selectedMonth: null,
      attention: null,
      spotChecks: [],
      outputs: [],
      runs: [],
      outputsState: 'loaded',
      outputsGenerating: false,
      outputsPollingTimer: null,
      showAllVersions: false,
      outputsSort: { column: '', order: 'asc' },
      outputsFilter: { text: '', kind: '', reconType: '', runStatus: '' },
      overviewSort: { column: '', order: 'asc' },
      overviewTab: 'clients',
      overviewReconType: 'M365',
      overviewState: 'loaded',
      overviewItems: [],
      preview: { show: false, filename: '', item: null },
      previewLoader: null,
      isAdmin: false,
      formPricingReadiness: null,
      selectedPricingReadiness: null
    };
  },
  methods: {
    formatCurrency: function(amount, currency) { return formatCurrency(amount, currency); },
    formatDate: formatDate,
    kindLabel: function(kind) {
      var labels = {
        client_detail: 'Client detail',
        intacct_upload: 'Intacct upload',
        ap_upload: 'AP upload',
        combined_workbook: 'Combined workbook',
        accrual_variance: 'Accrual variance'
      };
      return labels[kind] || kind;
    },
    entityLabel: function(item) {
      return item.customerName || item.intacctEntity || 'Combined';
    },
    sortState: function(state, column) {
      if (state.column === column) {
        state.order = state.order === 'asc' ? 'desc' : 'asc';
      } else {
        state.column = column;
        state.order = 'asc';
      }
    },
    sortOutputs: function(column) { this.sortState(this.outputsSort, column); },
    outputsSortIndicator: function(column) {
      return this.outputsSort.column === column ? (this.outputsSort.order === 'asc' ? '▲' : '▼') : '';
    },
    sortOverview: function(column) { this.sortState(this.overviewSort, column); },
    overviewSortIndicator: function(column) {
      return this.overviewSort.column === column ? (this.overviewSort.order === 'asc' ? '▲' : '▼') : '';
    },
    setOverviewTab: function(tab) {
      this.overviewTab = tab;
      this.overviewSort = { column: '', order: 'asc' };
      this.loadOverview();
    },
    triggerProcess: function() {
      var self = this;
      this.processing = true;
      this.processResult = null;
      this.error = '';
      var baseParams = {
        billingMonth: this.form.billingMonth
      };
      if (this.form.regionCode) baseParams.regionCode = this.form.regionCode;

      var m365Promise = processRecon(Object.assign({ reconType: 'M365' }, baseParams));
      var azurePromise = processRecon(Object.assign({ reconType: 'Azure' }, baseParams));

      Promise.allSettled([m365Promise, azurePromise]).then(function(results) {
        self.processing = false;
        var result = {};
        if (results[0].status === 'fulfilled') {
          result.m365 = results[0].value;
        } else {
          result.m365 = { rawRowCount: 0, trimmedRowCount: 0, processedRowCount: 0, clientCount: 0, errors: [results[0].reason ? results[0].reason.message : 'M365 processing failed'] };
        }
        if (results[1].status === 'fulfilled') {
          result.azure = results[1].value;
        } else {
          result.azure = { rawRowCount: 0, trimmedRowCount: 0, processedRowCount: 0, clientCount: 0, errors: [results[1].reason ? results[1].reason.message : 'Azure processing failed'] };
        }
        self.processResult = result;
        if (result.m365.errors.length === 0 && result.azure.errors.length === 0) {
          self.successMsg = 'Recon processed for ' + self.form.billingMonth + ' (M365 + Azure).';
          setTimeout(function() { self.successMsg = ''; }, 5000);
        }
        self.loadStatus();
        if (self.selectedMonth && self.selectedMonth.billing_month === self.form.billingMonth) {
          self.loadAttention();
          self.loadSpotChecks();
          self.loadOutputs();
          self.loadOverview();
        }
      });
    },
    loadStatus: function() {
      var self = this;
      this.statusState = 'loading';
      getReconStatus().then(function(data) {
        self.statusItems = data.items || [];
        self.statusState = 'loaded';
      }).catch(function(err) {
        self.error = err.message || 'Failed to load status.';
        self.statusState = 'loaded';
      });
    },
    loadPricingReadiness: function() {
      var self = this;
      this.formPricingReadiness = null;
      if (!this.form.billingMonth) return;
      getNcePricingReadiness(this.form.billingMonth).then(function(r) {
        self.formPricingReadiness = r;
      }).catch(function() {
        self.formPricingReadiness = null;
      });
    },
    loadSelectedPricingReadiness: function() {
      var self = this;
      this.selectedPricingReadiness = null;
      if (!this.selectedMonth) return;
      getNcePricingReadiness(this.selectedMonth.billing_month).then(function(r) {
        self.selectedPricingReadiness = r;
      }).catch(function() {
        self.selectedPricingReadiness = null;
      });
    },
    selectMonth: function(item) {
      this.selectedMonth = item;
      this.overviewTab = 'clients';
      this.overviewSort = { column: '', order: 'asc' };
      this.loadAttention();
      this.loadSpotChecks();
      this.loadOutputs();
      this.loadSelectedPricingReadiness();
      this.loadOverview();
    },
    loadAttention: function() {
      var self = this;
      this.attention = null;
      if (!this.selectedMonth) return;
      var month = this.selectedMonth.billing_month;
      Promise.allSettled([
        getReconAttention({ reconType: 'M365', billingMonth: month }),
        getReconAttention({ reconType: 'Azure', billingMonth: month })
      ]).then(function(results) {
        var unlisted = [];
        var incompleteByClient = {};
        results.forEach(function(r) {
          if (r.status !== 'fulfilled' || !r.value) return;
          (r.value.unlisted || []).forEach(function(name) {
            if (unlisted.indexOf(name) === -1) unlisted.push(name);
          });
          (r.value.incomplete || []).forEach(function(c) {
            if (!incompleteByClient[c.clientName]) incompleteByClient[c.clientName] = [];
            c.missing.forEach(function(m) {
              if (incompleteByClient[c.clientName].indexOf(m) === -1) incompleteByClient[c.clientName].push(m);
            });
          });
        });
        self.attention = {
          unlisted: unlisted,
          incomplete: Object.keys(incompleteByClient).sort().map(function(name) {
            return { clientName: name, missing: incompleteByClient[name] };
          })
        };
      });
    },
    loadSpotChecks: function() {
      var self = this;
      this.spotChecks = [];
      if (!this.selectedMonth) return;
      getReconSpotChecks({ billingMonth: this.selectedMonth.billing_month }).then(function(data) {
        self.spotChecks = data.items || [];
      }).catch(function() {
        self.spotChecks = [];
      });
    },
    loadOutputs: function() {
      var self = this;
      if (!this.selectedMonth) return;
      this.outputsState = 'loading';
      var month = this.selectedMonth.billing_month;
      Promise.allSettled([
        listReconOutputs({ billingMonth: month }),
        listReconOutputRuns({ billingMonth: month })
      ]).then(function(results) {
        self.outputs = (results[0].status === 'fulfilled' && results[0].value.items) || [];
        self.runs = (results[1].status === 'fulfilled' && results[1].value.items) || [];
        self.outputsState = 'loaded';
        var running = self.outputs.some(function(o) { return o.runStatus === 'running'; })
          || self.runs.some(function(r) { return r.status === 'running'; });
        if (running) {
          self.outputsGenerating = true;
          self.scheduleOutputsPoll();
        } else {
          self.outputsGenerating = false;
        }
      });
    },
    scheduleOutputsPoll: function() {
      var self = this;
      if (this.outputsPollingTimer) clearTimeout(this.outputsPollingTimer);
      this.outputsPollingTimer = setTimeout(function() { self.loadOutputs(); }, 5000);
    },
    generateOutputs: function() {
      var self = this;
      this.error = '';
      this.outputsGenerating = true;
      generateReconOutputs(this.selectedMonth.billing_month).then(function() {
        self.successMsg = 'Generation started for ' + self.selectedMonth.billing_month + ' — files appear below as they land.';
        self.scheduleOutputsPoll();
        setTimeout(function() { self.successMsg = ''; }, 5000);
      }).catch(function(err) {
        self.outputsGenerating = false;
        self.error = (err && err.message) ? err.message : 'Failed to start generation.';
        if (err && err.code === 'GENERATION_IN_PROGRESS') {
          self.error = 'A generation run for this month is already in progress.';
        }
      });
    },
    downloadOutput: function(item) {
      var self = this;
      downloadReconOutputFile(item.id).then(function(r) {
        self.downloadBlob(r, item.contentType && item.contentType.indexOf('sheet') > 0 ? 'xlsx' : 'csv');
      }).catch(function(err) { self.error = err.message; });
    },
    previewOutput: function(item) {
      var self = this;
      this.preview = { show: true, filename: item.filename, item: item };
      this.previewLoader = function() {
        return downloadReconOutputFile(item.id).then(function(r) {
          var blob = r.data;
          var isCsv = /\.csv$/i.test(item.filename);
          if (isCsv) {
            return blob.text().then(function(text) { return { text: text }; });
          }
          return blob.arrayBuffer().then(function(buf) { return { arrayBuffer: buf }; });
        }).catch(function(err) { self.error = err.message; throw err; });
      };
    },
    deleteFile: function(item) {
      var self = this;
      if (!confirm('Delete "' + item.filename + '" (v' + item.version + ')? The blob is soft-deleted (recoverable for 14 days); the registry row is removed immediately.')) return;
      deleteReconOutput(item.id).then(function() {
        self.successMsg = 'Deleted ' + item.filename;
        setTimeout(function() { self.successMsg = ''; }, 4000);
        self.loadOutputs();
      }).catch(function(err) {
        self.error = err.code === 'BILLING_DELETE_DENIED'
          ? 'Only Cyanic admins may delete generated files.'
          : (err.message || 'Delete failed.');
      });
    },
    deleteRun: function(run) {
      var self = this;
      if (!confirm('Delete the ENTIRE ' + run.runId.slice(0, 8) + '… run for ' + run.billingMonth + ' (all files of that run, incl. version history rows)? Blobs are soft-deleted (recoverable for 14 days).')) return;
      deleteReconOutputRun(run.runId).then(function(res) {
        self.successMsg = 'Run deleted (' + (res.filesDeleted || '?') + ' files).';
        setTimeout(function() { self.successMsg = ''; }, 4000);
        self.loadOutputs();
      }).catch(function(err) {
        self.error = err.code === 'BILLING_DELETE_DENIED'
          ? 'Only Cyanic admins may delete generated files.'
          : (err.message || 'Delete failed.');
      });
    },
    downloadBundle: function(kind, reconType) {
      var self = this;
      var params = { billingMonth: this.selectedMonth.billing_month, kind: kind };
      if (reconType) params.reconType = reconType;
      downloadReconOutputBundle(params).then(function(r) {
        var disposition = r.headers && r.headers['content-disposition'];
        var filename = kind + (reconType ? '_' + reconType : '') + '_' + self.selectedMonth.billing_month + '.zip';
        if (disposition) {
          var match = disposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }
        var blob = new Blob([r.data], { type: 'application/zip' });
        var url = window.URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }).catch(function(err) { self.error = err.message; });
    },
    loadOverview: function() {
      var self = this;
      if (!this.selectedMonth) return;
      this.overviewState = 'loading';
      var apiFn;
      if (this.overviewTab === 'clients') {
        apiFn = getReconClientOverview({ reconType: this.overviewReconType, billingMonth: this.selectedMonth.billing_month });
      } else if (this.overviewTab === 'products') {
        apiFn = getReconProductOverview({ reconType: this.overviewReconType, billingMonth: this.selectedMonth.billing_month });
      } else if (this.overviewTab === 'ap') {
        apiFn = getReconApOverview({ billingMonth: this.selectedMonth.billing_month });
      }
      if (apiFn) {
        apiFn.then(function(data) {
          self.overviewItems = data.items || [];
          self.overviewState = 'loaded';
        }).catch(function() {
          self.overviewItems = [];
          self.overviewState = 'loaded';
        });
      }
    },
    downloadBlob: function(response, type) {
      var disposition = response.headers && response.headers['content-disposition'];
      var filename = 'recon_export.' + type;
      if (disposition) {
        var match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      var blob = new Blob([response.data], { type: type === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' });
      var url = window.URL.createObjectURL(blob);
      var link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    downloadClients: function() {
      var self = this;
      var params = {
        reconType: this.overviewReconType,
        billingMonth: this.selectedMonth.billing_month,
        includeDnb: this.genOptions.includeDnb,
        includeOtl: this.genOptions.includeOtl,
        splitMonth: this.genOptions.splitMonth,
        timestampFiles: this.genOptions.timestampFiles
      };
      downloadReconClientWorkbook(params).then(function(r) {
        self.downloadBlob(r, 'xlsx');
      }).catch(function(err) { self.error = err.message; });
    },
    downloadIntacct: function() {
      var self = this;
      var params = {
        reconType: this.overviewReconType,
        billingMonth: this.selectedMonth.billing_month,
        splitMonth: this.genOptions.splitMonth
      };
      downloadReconIntacctCsv(params).then(function(r) {
        self.downloadBlob(r, 'csv');
      }).catch(function(err) { self.error = err.message; });
    },
    downloadAp: function() {
      var self = this;
      var params = {
        billingMonth: this.selectedMonth.billing_month,
        splitMonth: this.genOptions.splitMonth
      };
      downloadReconApCsv(params).then(function(r) {
        self.downloadBlob(r, 'csv');
      }).catch(function(err) {
        if (err && err.code === 'INVOICE_VALIDATION_FAILED') {
          self.error = 'Invoice validation failed: ' + (err.mismatches || []).join('; ');
        } else {
          self.error = err.message;
        }
      });
    }
  },
  mounted: function() {
    var self = this;
    this.loadStatus();
    this.loadPricingReadiness();
    canDeleteOutputs().then(function(v) { self.isAdmin = v; });
  },
  beforeUnmount: function() {
    if (this.outputsPollingTimer) clearTimeout(this.outputsPollingTimer);
  }
};
</script>

<style scoped>
.recon-processing { min-height: 100%; }
.page-body { padding: 24px; }
.card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); margin-bottom: 20px;
}
.card h3 { font-size: 0.9375rem; font-weight: 700; color: #0f172a; margin: 0 0 8px 0; }
.card-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.card-header-row h3 { margin: 0; }
.header-actions { display: flex; gap: 12px; align-items: center; }
.step-desc { font-size: 0.8125rem; color: #64748b; margin: 0 0 16px 0; }
.process-form { display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; }
.filter-select, .filter-input {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px;
  font-size: 0.875rem; font-family: 'Inter', sans-serif;
}
.process-btn {
  background: #4f46e5; color: #fff; border: none; padding: 10px 24px;
  border-radius: 10px; font-size: 0.875rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.process-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.process-result {
  margin-top: 16px; padding: 16px; background: #f0fdf4; border-radius: 8px;
  border: 1px solid #bbf7d0; font-size: 0.875rem;
}
.process-result.process-error { background: #fef2f2; border-color: #fecaca; }
.result-block { display: flex; align-items: center; gap: 16px; margin-bottom: 12px; flex-wrap: wrap; }
.result-block:last-child { margin-bottom: 0; }
.result-row { color: #64748b; }
.result-row span { font-weight: 600; }
.result-row.error { color: #991b1b; }
.error-list { margin-top: 8px; color: #991b1b; font-size: 0.8125rem; width: 100%; }
.type-badge {
  display: inline-block; padding: 2px 10px; border-radius: 6px; font-size: 0.6875rem;
  font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
}
.type-badge.m365 { background: #dbeafe; color: #1e40af; }
.type-badge.azure { background: #fef3c7; color: #92400e; }
.gen-hero-row { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
.generate-hero-btn {
  background: #4f46e5; color: #fff; border: none; padding: 12px 32px;
  border-radius: 10px; font-size: 0.9375rem; font-weight: 700; cursor: pointer;
  font-family: 'Inter', sans-serif; letter-spacing: 0.01em;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}
.generate-hero-btn:hover:not(:disabled) { background: #4338ca; }
.generate-hero-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.gen-hero-desc { font-size: 0.8125rem; color: #64748b; max-width: 720px; }
.lastrun-row {
  margin-top: 14px; padding: 10px 14px; border-radius: 8px; font-size: 0.8125rem;
  background: #f8fafc; border: 1px solid #e2e8f0; color: #334155;
}
.lastrun-row.complete { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
.lastrun-row.failed { background: #fef2f2; border-color: #fecaca; color: #991b1b; }
.lastrun-row.running { background: #eff6ff; border-color: #bfdbfe; color: #1e40af; }
.lastrun-error { margin-top: 6px; font-size: 0.75rem; }
.gen-options { display: flex; gap: 20px; flex-wrap: wrap; margin: 12px 0; }
.toggle-label { font-size: 0.8125rem; color: #475569; display: flex; align-items: center; gap: 6px; cursor: pointer; }
.toggle-label input { width: 16px; height: 16px; }
.file-gen-buttons { display: flex; gap: 12px; flex-wrap: wrap; }
.gen-btn {
  background: #f1f5f9; color: #4f46e5; border: 1px solid #e2e8f0; padding: 12px 20px;
  border-radius: 10px; cursor: pointer; font-family: 'Inter', sans-serif;
  display: flex; flex-direction: column; gap: 4px; text-align: left;
}
.gen-btn:hover { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.gen-btn-title { font-size: 0.875rem; font-weight: 600; }
.gen-btn-desc { font-size: 0.6875rem; color: #94a3b8; }
.gen-btn:hover .gen-btn-desc { color: #e0e7ff; }
.gen-note { font-size: 0.75rem; color: #6366f1; background: #eef2ff; padding: 10px 14px; border-radius: 8px; border: 1px solid #c7d2fe; margin-top: 12px; }
.legacy-summary { cursor: pointer; font-size: 0.8125rem; font-weight: 600; color: #64748b; }
.legacy-summary:hover { color: #4f46e5; }
details { font-size: 0.8125rem; }
details[open] .legacy-summary { margin-bottom: 4px; }
.attention-banner {
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e;
  padding: 12px 16px; border-radius: 8px; font-size: 0.8125rem; margin-bottom: 14px;
  display: flex; flex-direction: column; gap: 4px;
}
.attention-hint { font-size: 0.75rem; color: #b45309; margin-top: 4px; }
.pricing-gate-banner {
  background: #fef2f2; border: 1px solid #fecaca; color: #991b1b;
  padding: 12px 16px; border-radius: 8px; font-size: 0.8125rem; margin-bottom: 14px;
}
.pricing-gate-note {
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e;
  padding: 10px 14px; border-radius: 8px; font-size: 0.8125rem; margin-bottom: 14px;
}
.spotcheck-strip { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
.spotcheck-item {
  padding: 8px 12px; border-radius: 6px; font-size: 0.75rem;
  background: #f8fafc; border: 1px solid #e2e8f0; color: #334155;
}
.spotcheck-item.warn { background: #fffbeb; border-color: #fde68a; color: #92400e; }
.outputs-toolbar { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.outputs-filters { display: flex; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; align-items: center; }
.outputs-filter-text { flex: 1; min-width: 200px; max-width: 340px; }
.outputs-count { font-size: 0.75rem; color: #64748b; }
th.sortable { cursor: pointer; user-select: none; }
th.sortable:hover { color: #4f46e5; }
.sort-ind { color: #4f46e5; font-size: 0.625rem; margin-left: 2px; }
.toolbar-btn {
  background: #f1f5f9; color: #4f46e5; border: 1px solid #e2e8f0; padding: 6px 16px;
  border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.toolbar-btn:hover { background: #e0e7ff; border-color: #c7d2fe; }
.filename-cell { font-size: 0.75rem; font-family: ui-monospace, monospace; }
.run-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.6875rem; font-weight: 700; }
.run-badge.complete { background: #dcfce7; color: #166534; }
.run-badge.running { background: #dbeafe; color: #1e40af; }
.run-badge.failed { background: #fef2f2; color: #991b1b; }
.delete-btn {
  background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; padding: 4px 12px;
  border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer; margin-left: 4px;
}
.delete-btn:hover { background: #fee2e2; }
.delete-run-btn {
  background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; padding: 6px 14px;
  border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.delete-run-btn:hover { background: #fee2e2; }
.overview-tabs { display: flex; gap: 4px; }
.overview-tabs button {
  background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; padding: 6px 16px;
  border-radius: 8px; font-size: 0.75rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.overview-tabs button.active { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.recon-type-switch { display: flex; gap: 4px; margin-bottom: 12px; }
.recon-type-switch button {
  background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; padding: 4px 14px;
  border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.recon-type-switch button.active { background: #e0e7ff; color: #4338ca; border-color: #c7d2fe; }
.config-table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.8125rem; }
.data-table th { text-align: left; padding: 10px 12px; background: #f1f5f9; color: #475569; font-weight: 700; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.data-table tbody tr { cursor: pointer; transition: background 0.15s; }
.data-table tbody tr:hover { background: #f8fafc; }
.data-table tbody tr.selected { background: #eff6ff; }
.data-table tbody tr:last-child td { border-bottom: none; }
.summary-btn { background: #f1f5f9; color: #4f46e5; border: 1px solid #e2e8f0; padding: 4px 12px; border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer; }
.summary-btn:hover { background: #e0e7ff; border-color: #c7d2fe; }
.no-data { font-size: 0.875rem; color: #94a3b8; text-align: center; padding: 32px 16px; background: #f8fafc; border-radius: 8px; border: 1px dashed #e2e8f0; }
.error-message { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; padding: 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 16px; }
.success-message { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; padding: 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 16px; }
.loading-state { color: #64748b; font-size: 0.875rem; padding: 24px; }
</style>
